import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { saveUserProfile, getUserProfile, updateUserVerificationStatus } from '../utils/saveUserProfile';
import { validateUsernameContent } from '../utils/usernameModeration';
import {
  trackSignupStarted,
  trackSignupCompleted,
  trackSigninCompleted,
  trackSignout,
  trackEmailVerificationSent,
  trackEmailVerified,
  trackAuthError,
  setAnalyticsUserId,
  updateUserStats
} from '../utils/analytics';

const AuthContext = createContext();

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

  // Sign up function with Howard email validation
  async function signup(email, password, username) {
    // Track signup started
    trackSignupStarted('email');

    // Validate username content (profanity filter and reserved names)
    const usernameValidation = validateUsernameContent(username);
    if (!usernameValidation.isValid) {
      trackAuthError('signup_validation', usernameValidation.reason);
      throw new Error(usernameValidation.reason);
    }

    // Validate Howard email domain
    if (!email.endsWith('@bison.howard.edu')) {
      trackAuthError('signup_validation', 'Invalid email domain');
      throw new Error('You must use a @bison.howard.edu email address');
    }

    // Email validation regex
    const emailRegex = /^[^\s@]+@bison\.howard\.edu$/;
    if (!emailRegex.test(email)) {
      trackAuthError('signup_validation', 'Invalid email format');
      throw new Error('Invalid email format');
    }

    // Password validation (min 6 chars, at least 1 number and 1 special char)
    if (password.length < 6) {
      trackAuthError('signup_validation', 'Password too short');
      throw new Error('Password must be at least 6 characters');
    }
    if (!/\d/.test(password)) {
      trackAuthError('signup_validation', 'Password missing number');
      throw new Error('Password must contain at least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      trackAuthError('signup_validation', 'Password missing special character');
      throw new Error('Password must contain at least one special character');
    }

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);
      trackEmailVerificationSent();

      // Save user profile to Firestore
      await saveUserProfile(user.uid, email, username);

      // Track signup completed
      trackSignupCompleted(user.uid, username);

      return user;
    } catch (error) {
      // Handle specific Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        trackAuthError(error.code, 'Email already registered');
        throw new Error('This email is already registered');
      } else if (error.code === 'auth/invalid-email') {
        trackAuthError(error.code, 'Invalid email address');
        throw new Error('Invalid email address');
      } else if (error.code === 'auth/weak-password') {
        trackAuthError(error.code, 'Weak password');
        throw new Error('Password is too weak');
      }
      trackAuthError('signup_error', error.message);
      throw error;
    }
  }

  // Sign in function
  async function signin(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Track successful sign in
      trackSigninCompleted(userCredential.user.uid, 'email');

      return userCredential.user;
    } catch (error) {
      // Track auth errors
      if (error.code === 'auth/user-not-found') {
        trackAuthError(error.code, 'User not found');
        throw new Error('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        trackAuthError(error.code, 'Wrong password');
        throw new Error('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        trackAuthError(error.code, 'Invalid email');
        throw new Error('Invalid email address');
      } else if (error.code === 'auth/invalid-credential') {
        trackAuthError(error.code, 'Invalid credentials');
        throw new Error('Invalid email or password');
      }
      trackAuthError('signin_error', error.message);
      throw error;
    }
  }

  // Sign out function
  async function logout() {
    trackSignout();
    return signOut(auth);
  }

  // Resend verification email
  async function resendVerificationEmail() {
    if (currentUser) {
      await sendEmailVerification(currentUser);
      trackEmailVerificationSent();
    }
  }

  // Listen for auth state changes and fetch user profile
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Set analytics user ID
        setAnalyticsUserId(user.uid);

        // Fetch user profile from Firestore
        try {
          const profile = await getUserProfile(user.uid);

          // If profile doesn't exist, user needs to set up their profile
          if (!profile) {
            setUserProfile(null);
            setNeedsProfileSetup(true);
          } else {
            // If email is verified but Firestore doesn't reflect it, update it
            if (user.emailVerified && !profile.isVerified) {
              await updateUserVerificationStatus(user.uid);
              // Refetch profile to get updated isVerified status
              const updatedProfile = await getUserProfile(user.uid);
              setUserProfile(updatedProfile);

              // Track email verification
              trackEmailVerified(user.uid);
            } else {
              setUserProfile(profile);
            }

            // Update analytics with user stats
            if (profile.stats) {
              updateUserStats(profile.stats);
            }

            setNeedsProfileSetup(false);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
          setNeedsProfileSetup(false);
        }
      } else {
        setUserProfile(null);
        setNeedsProfileSetup(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Function to refresh user profile (called after ProfileSetup completes)
  async function refreshUserProfile() {
    if (currentUser) {
      try {
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
        setNeedsProfileSetup(!profile);
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    }
  }

  const value = {
    currentUser,
    userProfile,
    loading,
    needsProfileSetup,
    signup,
    signin,
    logout,
    resendVerificationEmail,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
